import { useEffect, useRef, useState } from "react";
import { fetchProtectedAssetBlob } from "@/lib/api";

// Small module-level cache so switching between lessons doesn't re-fetch an
// already-loaded slide/video, and so we can revoke object URLs on unmount
// without leaking memory.
const cache = new Map();

// Loads a gated course asset (served from the private R2 bucket via the
// Worker) and returns a blob: object URL usable as an <img>/<video> src.
export function useProtectedAsset(key) {
  const [url, setUrl] = useState(() => cache.get(key) || null);
  const [loading, setLoading] = useState(!cache.has(key));
  const [error, setError] = useState(null);
  const keyRef = useRef(key);
  keyRef.current = key;

  useEffect(() => {
    if (!key) return;
    if (cache.has(key)) {
      setUrl(cache.get(key));
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchProtectedAssetBlob(key)
      .then((blob) => {
        if (cancelled) return;
        const objectUrl = URL.createObjectURL(blob);
        cache.set(key, objectUrl);
        setUrl(objectUrl);
      })
      .catch((e) => {
        if (!cancelled) setError(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [key]);

  return { url, loading, error };
}
